import {html} from 'lit';
import Base from '../base.js';
import styles from './button.css' assert {type: 'css'};

/**
 * @element inv-button
 *
 * @class Button
 * @classdesc This is a web component for a button.
 *
 * @property {boolean} disabled - If true, disables the button.
 *
 * @cssclass .outlined - Outlines the button.
 * @cssclass .large - Adjusts the size of the button to large.
 * @cssclass .medium - Adjusts the size of the button to medium.
 * @cssclass .small - Adjusts the size of the button to small.
 * @cssclass .full-width - Adjusts the button's width to be 100% of the parent container.
 *
 * @cssproperty --inv-button-contained-color
 * @description The font color for the button in its normal state.
 * @type {string}
 * @default "#fff"
 *
 * @cssproperty --inv-font-family
 * @description The font family for the button text.
 * @type {string}
 * @default "'Inter'"
 *
 * @cssproperty --inv-button-font-size
 * @description The font size for the button text.
 * @type {string}
 * @default "1em"
 *
 * @cssproperty --inv-button-contained-background
 * @description The background gradient for the button in its normal state.
 * @type {string}
 * @default "linear-gradient(93.64deg, #00d5e0 1%, #15a6da 26.85%, #004bba 86.58%, #0f4595 99.6%)"
 *
 * @cssproperty --inv-button-contained-border-radius
 * @description The border radius for the button in its normal state.
 * @type {string}
 * @default "1.8em"
 *
 * @cssproperty --inv-button-contained-hover-background
 * @description The background color for the button in its hover state (if enabled).
 * @type {string}
 * @default "#0e4dab"
 *
 * @cssproperty --inv-button-outlined-color
 * @description The font color for an outlined button.
 * @type {string}
 * @default "#0e4dab"
 *
 * @cssproperty --inv-button-outlined-background
 * @description The background gradient for an outlined button.
 * @type {string}
 * @default "linear-gradient(93.64deg, #00d5e0 1%, #15a6da 26.85%, #004bba 86.58%, #0f4595 99.6%)"
 *
 * @cssproperty --inv-button-outlined-hover-background
 * @description The background color for an outlined button in its hover state (if enabled).
 * @type {string}
 * @default "#0e4dab"
 *
 * @cssproperty --inv-button-outlined-border-radius
 * @description The border radius for an outlined button.
 * @type {string}
 * @default "2.33em"
 */

export default class Button extends Base {
    static get properties() {
        return {
            disabled: {
                type: Boolean,
                reflect: true,
            },
        };
    }

    getStyleSheets() {
        return [...super.getStyleSheets(), styles];
    }

    render() {
        return html`
            <button type="button" ?disabled=${this.disabled}>
                <slot></slot>
            </button>
        `;
    }
}

Button.register();
